import * as React from 'react';
import { X, Loader2 } from 'lucide-react';
import { DynamicForm } from '../forms/dynamic-form';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export interface SliderFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  collection: string;
  mode: 'create' | 'edit';
  initialValues?: Record<string, unknown>;
  excludeFields?: string[];
  onSubmit: (values: Record<string, unknown>) => void | Promise<void>;
  isSubmitting?: boolean;
}

export const SliderForm = React.forwardRef<HTMLDivElement, SliderFormProps>(
  (
    {
      open,
      onOpenChange,
      title,
      collection,
      mode,
      initialValues,
      excludeFields,
      onSubmit,
      isSubmitting = false,
    },
    ref,
  ) => {
    const handleClose = () => {
      if (!isSubmitting) {
        onOpenChange(false);
      }
    };

    const handleSubmit = async (values: Record<string, unknown>) => {
      try {
        await onSubmit(values);
      } catch (error) {
        console.error('Form submission error:', error);
      }
    };

    return (
      <div ref={ref}>
        {/* Overlay */}
        {open && (
          <div
            className="fixed inset-0 z-40 bg-black/50 transition-opacity"
            onClick={handleClose}
          />
        )}

        {/* Slider panel */}
        <div
          className={cn(
            'fixed right-0 top-0 z-50 h-screen w-full max-w-2xl flex flex-col bg-[var(--color-card)] shadow-lg transition-transform duration-300',
            open ? 'translate-x-0' : 'translate-x-full',
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[var(--color-border)] px-6 py-4 flex-shrink-0">
            <h2 className="text-xl font-semibold text-[var(--color-foreground)]">
              {title}
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              disabled={isSubmitting}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Form content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <DynamicForm
              collection={collection}
              mode={mode}
              initialValues={initialValues}
              excludeFields={excludeFields}
              onSubmit={handleSubmit}
            />
          </div>

          {/* Footer with submit button */}
          <div className="border-t border-[var(--color-border)] px-6 py-4 flex-shrink-0 flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={`form-${collection}-${mode}`}
              disabled={isSubmitting}
              className="min-w-24"
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    );
  },
);

SliderForm.displayName = 'SliderForm';
